/**
 * ledgerLeaderboard.js — pure, stateless leaderboard computation for the
 * LedgerDashboardPage.
 *
 * computeFundRank(fund) -> FundRank
 * rankFunds(portals, sortMetric, topN) -> FundRank[]  (original, used by tests)
 * sortFunds(portals, metric) -> { portal, funds[] }[] (new — powers the "Sort by" dropdown)
 *
 * FundRank shape:
 *   {
 *     rank:        number    — 1-based rank after sort
 *     portal:      string    — portal id
 *     fund_name:   string
 *     currency:    string
 *     invested:    number    — Σ members.capital_contributed_cumulative
 *     distributed: number    — fund.total_distributed  (may be 0)
 *     nav:         number    — fund.total_nav           (may be 0)
 *     tvpi:        number|null — (distributed + nav) / invested; null if invested <= 0
 *     dpi:         number|null — distributed / invested; null if invested <= 0
 *     nav_inv:     number|null — nav / invested (RVPI); null if invested <= 0 or nav null
 *   }
 *
 * SORT_METRICS — legacy list used by rankFunds and LeaderboardTab (unchanged).
 *   "tvpi" | "dpi" | "nav" | "distributed" | "invested"
 *
 * FUND_SORT_METRICS — new list for the "Sort by" dropdown in the funds list.
 *   "currentValue" | "invested" | "distributed" | "tvpi" | "dpi" | "value_inv"
 *
 * Performance metrics (tvpi, dpi, value_inv): funds with no data (invested <= 0
 * or metric is null) are EXCLUDED from the list entirely.
 * Absolute metrics (currentValue, invested, distributed): all funds are shown,
 * null/zero ones sink to the bottom.
 */

// Legacy sort metrics (keep for existing tests + LeaderboardTab)
const SORT_METRICS = ["tvpi", "dpi", "nav", "distributed", "invested"];

// Sort metrics for the "Sort by" dropdown in the funds list view
const FUND_SORT_METRICS = [
  { value: "currentValue", label: "Current Value" },
  { value: "invested",     label: "Invested" },
  { value: "distributed",  label: "Distributed" },
  { value: "tvpi",         label: "TVPI" },
  { value: "dpi",          label: "DPI" },
  { value: "value_inv",    label: "Value/Inv" },
  { value: "xirr",         label: "Return %" },
];

// Performance metrics: when sorted by these, funds with no data are excluded entirely.
const PERFORMANCE_METRICS = new Set(["tvpi", "dpi", "value_inv", "xirr"]);

/**
 * Compute a fund-level FundRank row from a fund object.
 * Excludes discarded members from the invested sum (same as the visible page).
 */
export function computeFundRank(fund) {
  const members = (fund.members || []).filter((m) => !m.discarded);

  const invested = members.reduce((sum, m) => {
    // Use capital_contributed_cumulative, fall back to capital_called_cumulative
    const contrib = m.capital_contributed_cumulative ?? m.capital_called_cumulative ?? 0;
    return sum + Number(contrib || 0);
  }, 0);

  const distributed = Number(fund.total_distributed || 0);
  const nav = Number(fund.total_nav || 0);

  const tvpi = invested > 0 ? (distributed + nav) / invested : null;
  const dpi = invested > 0 ? distributed / invested : null;
  // nav_inv = NAV / Invested (RVPI — residual value to paid-in)
  const nav_inv = invested > 0 ? nav / invested : null;

  // xirr = irr of the largest-NAV member among non-discarded members where irr_reliable===true.
  // null when no such member exists (display ordering only — not a new aggregate figure).
  const reliableMembers = members.filter((m) => m.irr_reliable === true && m.irr != null);
  let xirr = null;
  if (reliableMembers.length > 0) {
    reliableMembers.sort((a, b) => Number(b.nav || 0) - Number(a.nav || 0));
    xirr = Number(reliableMembers[0].irr);
  }

  return {
    portal: fund.portal,
    fund_name: fund.fund_name,
    currency: fund.currency,
    invested,
    distributed,
    nav,
    tvpi,
    dpi,
    nav_inv,
    xirr,
  };
}

/**
 * Get the numeric sort value for a FundRank row given a metric key.
 * Returns -Infinity for nulls so they sink to the bottom.
 */
function sortValue(row, metric) {
  const v = row[metric];
  return v == null ? -Infinity : Number(v);
}

/**
 * Original rankFunds — unchanged for backward compatibility with existing tests.
 *
 * portals: data.portals array from /api/ledger/overview
 * sortMetric: "tvpi" | "dpi" | "nav" | "distributed" | "invested"  (default "tvpi")
 * topN: return only the top N results (0 = all)
 *
 * Returns an array of FundRank objects, sorted descending, with rank assigned.
 * Discarded funds (funds where all members are discarded) emit tvpi/dpi = null
 * and will sort to the bottom but are still included — callers filter if desired.
 */
export function rankFunds(portals, sortMetric = "tvpi", topN = 0) {
  const metric = SORT_METRICS.includes(sortMetric) ? sortMetric : "tvpi";

  // Flatten all non-empty funds across all portals
  const rows = [];
  for (const portal of portals || []) {
    for (const fund of portal.funds || []) {
      rows.push(computeFundRank(fund));
    }
  }

  // Sort descending by the chosen metric (nulls last)
  rows.sort((a, b) => sortValue(b, metric) - sortValue(a, metric));

  // Assign ranks
  const ranked = rows.map((row, i) => ({ ...row, rank: i + 1 }));

  // Apply topN cap
  if (topN > 0 && ranked.length > topN) {
    return ranked.slice(0, topN);
  }

  return ranked;
}

/**
 * sortFunds — reorders the portals tree (flat fund list) by the given metric.
 *
 * portals: data.portals array from /api/ledger/overview
 * metric: one of FUND_SORT_METRICS[*].value
 *   "currentValue" → fund.total_nav
 *   "invested"     → Σ members.capital_contributed_cumulative
 *   "distributed"  → fund.total_distributed
 *   "tvpi"         → (distributed + nav) / invested
 *   "dpi"          → distributed / invested
 *   "value_inv"    → nav / invested (RVPI)
 *
 * Returns a flat array of { fund, portalId } pairs in descending order.
 *
 * Exclusion rule (change 3):
 *   - Performance metrics (tvpi, dpi, value_inv): funds where the metric is null
 *     (i.e., invested <= 0) are EXCLUDED entirely.
 *   - Absolute metrics (currentValue, invested, distributed): all funds shown;
 *     zero/null values sink to the bottom.
 */
export function sortFunds(portals, metric = "currentValue") {
  // Build flat list of { fund, portalId, rank_row }
  const entries = [];
  for (const portal of portals || []) {
    for (const fund of portal.funds || []) {
      const rank = computeFundRank(fund);
      entries.push({ fund, portalId: portal.portal, rank });
    }
  }

  // Map metric name → FundRank key
  const rankKey = {
    currentValue: "nav",
    invested: "invested",
    distributed: "distributed",
    tvpi: "tvpi",
    dpi: "dpi",
    value_inv: "nav_inv",
    xirr: "xirr",
  }[metric] || "nav";

  // Exclusion for performance metrics: drop funds with null metric
  let visible = entries;
  if (PERFORMANCE_METRICS.has(metric)) {
    visible = entries.filter((e) => e.rank[rankKey] != null);
  }

  // Sort descending; null/zero sink to bottom for absolute metrics
  visible.sort((a, b) => {
    const av = a.rank[rankKey];
    const bv = b.rank[rankKey];
    // Null → bottom
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    return Number(bv) - Number(av);
  });

  return visible;
}

export { SORT_METRICS, FUND_SORT_METRICS };
